import StatusChip from 'components/@extended/StatusChip';
import { buttonStyle } from 'components/buttons/buttonStyle';
import EmptyState from 'components/EmptyState';
import { Add, CircleAlert, ListIcon, PencilLineIcon, TrashIcon } from 'components/icons';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import { useModal } from 'components/modals/useModal';
import { DataTable } from 'components/tables/data-table';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { Button } from 'components/ui/button';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    useDeleteInsiderFunctionAndReasonMutation,
    useGetInsiderFunctionAndReasonsQuery,
} from 'store/api/insider-function-and-reasons';
import { InsiderFunctionAndReasonDto } from 'types/matter-insider-list';
import { successAlert } from 'utils/alert';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { getInitials, useEmployeeNameFormatter } from 'utils/nameOrder';
import { useHasPermissions } from 'utils/permissionUtil';
import { CONFIGURATION_CARD_CLASS } from '../configurationCard';
import { SettingCaution } from '../settings/SettingCaution';
import FunctionAndReasonDialog from './FunctionAndReasonDialog';

/** The dialog's subject: a new entry, or the entry being edited. */
type DialogState = { entry?: InsiderFunctionAndReasonDto } | null;

function AppliesTo({ entry }: { entry: InsiderFunctionAndReasonDto }) {
    const formatName = useEmployeeNameFormatter();
    if (entry.isDefault) {
        return (
            <>
                <StatusChip
                    type="custom"
                    label={t('configuration.insider-list.default')}
                    className="bg-grey-200 text-primary new:bg-success-tint new:text-success-ink new:rounded-sm new:font-semibold font-semibold"
                />
                <span className="text-meta text-grey-600">
                    <FormattedMessage id="configuration.insider-list.default-everyone" />
                </span>
            </>
        );
    }
    // Every employee picked on the entry was deleted since.
    if (entry.employees.length === 0) {
        return <span className="text-fg-secondary">—</span>;
    }
    return entry.employees.map((employee) => {
        const name = formatName(employee);
        return (
            <span
                key={employee.id}
                title={name}
                className="bg-grey-200 text-grey-a400 new:bg-neutral-tint new:text-foreground-secondary inline-flex h-[22px] max-w-full min-w-0 items-center gap-1.5 rounded-full py-0 pr-2 pl-0.5 text-xs font-medium"
            >
                <span
                    aria-hidden
                    className="bg-paper text-grey-a400 new:bg-success-tint new:text-success-ink inline-flex size-[18px] shrink-0 items-center justify-center rounded-full text-[9px] font-semibold"
                >
                    {getInitials(name)}
                </span>
                <span className="truncate">{name}</span>
            </span>
        );
    });
}

/** The board's list glyph: smaller, finer and darker than the panel tier's own. */
const EmptyListIcon = ({ className }: { className?: string }) => (
    <ListIcon className={cn(className, 'text-grey-600 new:text-muted-foreground stroke-[1.75] text-[28px]')} />
);

/**
 * Configuration → Insider list: the function-and-reason entries an employee's insider-list row is filled from. Without
 * the back-office update the add button stays, disabled, and the rows carry no actions.
 */
export function InsiderListConfig(): JSX.Element {
    const { openModal, closeModal } = useModal();
    const { data: entries = [], isLoading, isError, refetch } = useGetInsiderFunctionAndReasonsQuery();
    const [deleteEntry] = useDeleteInsiderFunctionAndReasonMutation();
    const canEdit = useHasPermissions(['UPDATE.BACK_OFFICE_SETTINGS']);
    const [dialog, setDialog] = useState<DialogState>(null);
    const isEmpty = !isLoading && !isError && entries.length === 0;
    const hasDefault = entries.some((entry) => entry.isDefault);

    const addButton = (
        <Button
            {...buttonStyle(
                { variant: 'outlined' },
                'text-meta border-grey-300 text-primary new:text-foreground h-8 gap-1.5 px-3'
            )}
            // Not before the entries are in: the dialog reads them to know the default and who is taken.
            disabled={!canEdit || isLoading}
            onClick={() => setDialog({})}
        >
            <Add className="size-3.5" />
            <FormattedMessage id="configuration.insider-list.add" />
        </Button>
    );

    const columns = useMemo<DataTableColumnDef<InsiderFunctionAndReasonDto>[]>(() => {
        const confirmDelete = (entry: InsiderFunctionAndReasonDto): void => {
            openModal(
                <ConfirmationModal
                    content={t('configuration.insider-list.delete-confirmation')}
                    onCancel={closeModal}
                    onConfirm={() => {
                        void (async () => {
                            try {
                                await deleteEntry(entry.id).unwrap();
                                successAlert(t('configuration.insider-list.deleted'));
                            } catch {
                                // The API layer has already shown why.
                            }
                            closeModal();
                        })();
                    }}
                />,
                closeModal
            );
        };
        return [
            {
                header: t('configuration.insider-list.function-and-reason'),
                id: 'functionAndReason',
                accessorKey: 'functionAndReason',
                enableSorting: false,
                meta: { widthWeight: 5, absorbSlack: true },
                cell: ({ row }) => (
                    <span className="new:text-sm new:text-foreground whitespace-pre-wrap">
                        {row.original.functionAndReason}
                    </span>
                ),
            },
            {
                header: t('configuration.insider-list.applies-to'),
                id: 'appliesTo',
                enableSorting: false,
                meta: { widthWeight: 4 },
                cell: ({ row }) => (
                    <div className="flex min-h-5 flex-wrap items-center gap-1.5">
                        <AppliesTo entry={row.original} />
                    </div>
                ),
            },
            {
                header: '',
                id: 'actions',
                enableSorting: false,
                meta: { fixedPx: 96, textAlign: 'right' },
                cell: ({ row }) =>
                    canEdit ? (
                        <>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                className="text-grey-a400"
                                aria-label={t('edit')}
                                onClick={() => setDialog({ entry: row.original })}
                            >
                                <PencilLineIcon />
                            </Button>
                            <Button
                                variant="ghost"
                                size="icon-sm"
                                className="text-destructive"
                                aria-label={t('delete')}
                                onClick={() => confirmDelete(row.original)}
                            >
                                <TrashIcon />
                            </Button>
                        </>
                    ) : null,
            },
        ];
    }, [canEdit, deleteEntry, openModal, closeModal]);

    return (
        <>
            <h3 className="text-h3 text-balance">
                <FormattedMessage id="configuration.insider-list.title" />
            </h3>
            <section className="flex flex-col gap-2" aria-labelledby="configuration-insider-list-function-and-reason">
                <div className="flex min-h-8 flex-wrap items-start justify-between gap-4">
                    <div className="flex max-w-[680px] flex-col gap-0.5">
                        <h4 id="configuration-insider-list-function-and-reason" className="text-h5">
                            <FormattedMessage id="configuration.insider-list.function-and-reason" />
                        </h4>
                        <p className="text-meta text-grey-600 m-0">
                            <FormattedMessage id="configuration.insider-list.function-and-reason-description" />
                        </p>
                    </div>
                    {isEmpty || isError ? null : addButton}
                </div>
                <div className={cn(CONFIGURATION_CARD_CLASS, 'new:rounded-[10px]')}>
                    {isError ? (
                        <EmptyState
                            size="panel"
                            variant="error"
                            onRetry={() => void refetch()}
                            surface="configuration-insider-list"
                        />
                    ) : isEmpty ? (
                        <EmptyState
                            size="panel"
                            icon={EmptyListIcon}
                            titleId="configuration.insider-list.empty-title"
                            bodyId="configuration.insider-list.empty-body"
                            action={addButton}
                            surface="configuration-insider-list"
                        />
                    ) : (
                        <>
                            {isLoading || hasDefault ? null : (
                                <SettingCaution
                                    text={t('configuration.insider-list.no-default')}
                                    icon={<CircleAlert className="mt-px size-4" />}
                                    className="mx-4 mt-3 mb-3"
                                />
                            )}
                            <DataTable<InsiderFunctionAndReasonDto>
                                columns={columns}
                                data={entries}
                                getRowId={(entry) => String(entry.id)}
                                columnConfigId="configuration-insider-list"
                                ariaLabel={t('configuration.insider-list.function-and-reason')}
                                // The frame is the card's: the table draws no border of its own. Rows are
                                // top-aligned, as a wrapped text or a wrapped row of chips leaves them tall. The
                                // new design's head is in capitals and its rows are not striped; `!` as the table's
                                // own rules are unlayered and would beat a utility.
                                className="new:[&_thead_th]:uppercase! new:[&_thead_th]:tracking-[0.06em] new:[--dt-row-stripe:var(--dt-row-bg)]! rounded-none! border-0! [&_tbody_td]:items-start!"
                                isLoading={isLoading}
                                skeletonRows={2}
                            />
                        </>
                    )}
                </div>
            </section>
            {dialog ? (
                <FunctionAndReasonDialog entry={dialog.entry} entries={entries} onClose={() => setDialog(null)} />
            ) : null}
        </>
    );
}
